import { Injectable } from '@angular/core';

export const TDS_PROPERTY_CONSTRAINTS = {
  saleConsideration: { min: 100_000, max: 1_000_000_000, step: 100_000 },
  stampDutyValue:    { min: 0,       max: 1_000_000_000, step: 100_000 }
} as const;

export type SellerResidency = 'resident' | 'nri';
export type HoldingPeriod = 'longTerm' | 'shortTerm';

export interface StateStampDuty {
  name: string;
  stampDutyRate: number;
}

/**
 * Approximate general/male, urban stamp duty rates as of FY 2025-26. Actual rates vary by
 * gender concession, urban/rural classification, and change periodically by state notification -
 * always confirm the exact rate with the local sub-registrar office before a transaction. These
 * are used only to estimate the registration stamp duty cost; they do not affect the TDS rate
 * itself, since Section 393(1)/393(2) TDS on property sale is a flat national rate, not state-wise.
 */
export const INDIAN_STATE_STAMP_DUTY: StateStampDuty[] = [
  { name: 'Andaman & Nicobar Islands', stampDutyRate: 0.05 },
  { name: 'Andhra Pradesh', stampDutyRate: 0.05 },
  { name: 'Arunachal Pradesh', stampDutyRate: 0.06 },
  { name: 'Assam', stampDutyRate: 0.0825 },
  { name: 'Bihar', stampDutyRate: 0.057 },
  { name: 'Chandigarh', stampDutyRate: 0.06 },
  { name: 'Chhattisgarh', stampDutyRate: 0.05 },
  { name: 'Dadra & Nagar Haveli and Daman & Diu', stampDutyRate: 0.05 },
  { name: 'Delhi (NCT)', stampDutyRate: 0.06 },
  { name: 'Goa', stampDutyRate: 0.04 },
  { name: 'Gujarat', stampDutyRate: 0.049 },
  { name: 'Haryana', stampDutyRate: 0.07 },
  { name: 'Himachal Pradesh', stampDutyRate: 0.05 },
  { name: 'Jammu & Kashmir', stampDutyRate: 0.07 },
  { name: 'Jharkhand', stampDutyRate: 0.04 },
  { name: 'Karnataka', stampDutyRate: 0.05 },
  { name: 'Kerala', stampDutyRate: 0.08 },
  { name: 'Ladakh', stampDutyRate: 0.05 },
  { name: 'Madhya Pradesh', stampDutyRate: 0.075 },
  { name: 'Maharashtra', stampDutyRate: 0.06 },
  { name: 'Manipur', stampDutyRate: 0.07 },
  { name: 'Meghalaya', stampDutyRate: 0.099 },
  { name: 'Mizoram', stampDutyRate: 0.09 },
  { name: 'Nagaland', stampDutyRate: 0.0825 },
  { name: 'Odisha', stampDutyRate: 0.05 },
  { name: 'Puducherry', stampDutyRate: 0.08 },
  { name: 'Punjab', stampDutyRate: 0.07 },
  { name: 'Rajasthan', stampDutyRate: 0.06 },
  { name: 'Sikkim', stampDutyRate: 0.04 },
  { name: 'Tamil Nadu', stampDutyRate: 0.07 },
  { name: 'Telangana', stampDutyRate: 0.05 },
  { name: 'Tripura', stampDutyRate: 0.05 },
  { name: 'Uttar Pradesh', stampDutyRate: 0.07 },
  { name: 'Uttarakhand', stampDutyRate: 0.05 },
  { name: 'West Bengal', stampDutyRate: 0.07 }
];

// Section 393(1) of the Income-tax Act, 2025 (successor to the old Section 194-IA):
// TDS applies only when consideration is ₹50 lakh or more; resident sellers only.
const RESIDENT_TDS_THRESHOLD = 5_000_000;
const RESIDENT_RATE_WITH_PAN = 0.01;
const RESIDENT_RATE_NO_PAN   = 0.20; // Section 397(2), successor to the old Section 206AA

// Section 393(2) (successor to old Section 195, NRI seller): applies from Re. 1, no ₹50L threshold.
const NRI_LTCG_RATE = 0.125; // Sec 112, w.e.f. FY 2024-25, without indexation
const NRI_STCG_RATE = 0.30;  // highest slab, absent a lower/nil-deduction certificate

const CESS_RATE = 0.04;

// From 1 April 2026, the reporting form for Section 393(1) is Form 141 (Schedule B),
// which replaced the old Form 26QB. Deals whose TDS was deducted before that date remain
// governed by the old law/form; the deposit window, interest and fee rates are unchanged.
const NEW_FORM_EFFECTIVE_FROM = new Date(2026, 3, 1); // 1 Apr 2026

// TDS deposit is due within 30 days from the end of the month in which the deduction is made
// (unchanged by the Income-tax Act, 2025).
const TDS_DEPOSIT_WINDOW_DAYS = 30;
// Section 398(3)(a), successor to old Section 201(1A)(ii): interest for late deposit, per month or part.
const LATE_DEPOSIT_INTEREST_RATE_PER_MONTH = 0.015;
// Section 427, successor to old Section 234E: late filing fee for delay in furnishing the statement.
const LATE_FILING_FEE_PER_DAY = 200;

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
}

function monthsOrPartThereof(from: Date, to: Date): number {
  if (to <= from) return 0;
  return Math.max(1, Math.ceil(daysBetween(from, to) / 30));
}

/**
 * Surcharge on LTCG/STCG arising from immovable property is capped at 15% regardless of total
 * income (Finance Act 2023 capped the enhanced 25%/37% surcharge slabs for gains under Sections
 * 111A/112/112A). Consideration thresholds mirror the individual surcharge slabs up to that cap.
 */
function nriSurchargeRate(considerationForTds: number): number {
  if (considerationForTds > 10_000_000) return 0.15;
  if (considerationForTds > 5_000_000) return 0.10;
  return 0;
}

export interface TdsPropertyInputs {
  residency: SellerResidency;
  state: string;
  saleConsideration: number;
  stampDutyValue: number;
  panAvailable: boolean;
  holdingPeriod: HoldingPeriod;
  dateOfDeduction: string; // ISO yyyy-mm-dd — when the buyer deducted TDS (Form 141/26QB, resident sellers only)
  dateOfDeposit:   string; // ISO yyyy-mm-dd — when the buyer actually filed Form 141/26QB / deposited the TDS
}

export interface PropertyTdsFilingCompliance {
  formLabel:            string; // 'Form 141 (Schedule B)' on/after 1 Apr 2026, else the old 'Form 26QB'
  certificateLabel:     string; // 'Form 132' on/after 1 Apr 2026, else the old 'Form 16B'
  dueDate:              string; // ISO yyyy-mm-dd
  daysLate:             number;
  lateDepositInterest:  number;
  lateFilingFee:        number;
  totalPayable:         number; // tdsAmount + lateDepositInterest + lateFilingFee
}

export interface TdsPropertyResult {
  considerationForTds:    number;
  usedStampDutyValue:     boolean;
  tdsApplicable:          boolean;
  baseRate:                number;
  surchargeRate:           number;
  surchargeAmount:         number;
  cessAmount:              number;
  tdsAmount:               number;
  netAmountToSeller:       number;
  stateStampDutyRate:      number;
  estimatedStampDutyCost:  number;
  filingCompliance:        PropertyTdsFilingCompliance | null;
}

@Injectable({ providedIn: 'root' })
export class TdsPropertyCalculatorService {

  calculate(inputs: TdsPropertyInputs): TdsPropertyResult {
    const stampDutyValue = inputs.stampDutyValue || inputs.saleConsideration;
    const considerationForTds = Math.max(inputs.saleConsideration, stampDutyValue);
    const usedStampDutyValue = stampDutyValue > inputs.saleConsideration;

    const stateStampDutyRate = INDIAN_STATE_STAMP_DUTY.find(s => s.name === inputs.state)?.stampDutyRate ?? 0;
    const estimatedStampDutyCost = considerationForTds * stateStampDutyRate;

    if (inputs.residency === 'resident') {
      const tdsApplicable = considerationForTds >= RESIDENT_TDS_THRESHOLD;
      const baseRate = !tdsApplicable ? 0 : (inputs.panAvailable ? RESIDENT_RATE_WITH_PAN : RESIDENT_RATE_NO_PAN);
      const tdsAmount = considerationForTds * baseRate;

      return {
        considerationForTds, usedStampDutyValue, tdsApplicable,
        baseRate, surchargeRate: 0, surchargeAmount: 0, cessAmount: 0,
        tdsAmount, netAmountToSeller: inputs.saleConsideration - tdsAmount,
        stateStampDutyRate, estimatedStampDutyCost,
        filingCompliance: tdsApplicable ? this.calculateFilingCompliance(inputs, tdsAmount) : null
      };
    }

    const baseRate = inputs.holdingPeriod === 'longTerm' ? NRI_LTCG_RATE : NRI_STCG_RATE;
    const baseTax = considerationForTds * baseRate;
    const surchargeRate = nriSurchargeRate(considerationForTds);
    const surchargeAmount = baseTax * surchargeRate;
    const cessAmount = (baseTax + surchargeAmount) * CESS_RATE;
    const tdsAmount = baseTax + surchargeAmount + cessAmount;

    return {
      considerationForTds, usedStampDutyValue, tdsApplicable: true,
      baseRate, surchargeRate, surchargeAmount, cessAmount,
      tdsAmount, netAmountToSeller: inputs.saleConsideration - tdsAmount,
      stateStampDutyRate, estimatedStampDutyCost,
      filingCompliance: null // Form 141/26QB is specific to resident sellers under Sec 393(1); NRI sellers fall under Sec 393(2)/Form 27Q
    };
  }

  /**
   * Form 141 (Schedule B) is the challan-cum-statement a buyer files (and deposits TDS against)
   * for a resident seller under Section 393(1) of the Income-tax Act, 2025 — it replaced the old
   * Form 26QB under Section 194-IA from 1 April 2026. Deposit is due within 30 days from the end
   * of the month of deduction; missing it attracts interest under Sec 398(3)(a) (old Sec 201(1A)(ii))
   * and a late filing fee under Sec 427 (old Sec 234E). Prolonged non-filing can additionally draw
   * a discretionary penalty of ₹10,000–₹1,00,000 under Sec 461 (old Sec 271H), not modelled here.
   */
  private calculateFilingCompliance(inputs: TdsPropertyInputs, tdsAmount: number): PropertyTdsFilingCompliance {
    const dateOfDeduction = new Date(inputs.dateOfDeduction);
    const dateOfDeposit    = new Date(inputs.dateOfDeposit);
    const usesNewForm = dateOfDeduction >= NEW_FORM_EFFECTIVE_FROM;

    const dueDate = addDays(endOfMonth(dateOfDeduction), TDS_DEPOSIT_WINDOW_DAYS);
    const daysLate = Math.max(0, daysBetween(dueDate, dateOfDeposit));

    const lateDepositInterest = daysLate > 0
      ? tdsAmount * LATE_DEPOSIT_INTEREST_RATE_PER_MONTH * monthsOrPartThereof(dateOfDeduction, dateOfDeposit)
      : 0;
    const lateFilingFee = Math.min(daysLate * LATE_FILING_FEE_PER_DAY, tdsAmount);

    return {
      formLabel: usesNewForm ? 'Form 141 (Schedule B)' : 'Form 26QB',
      certificateLabel: usesNewForm ? 'Form 132' : 'Form 16B',
      dueDate: dueDate.toISOString().slice(0, 10),
      daysLate,
      lateDepositInterest,
      lateFilingFee,
      totalPayable: tdsAmount + lateDepositInterest + lateFilingFee
    };
  }
}
